/**
 * VehicleMovementTimeline — Explorer UI for the truth-layer movement projection.
 *
 * ┌────────────────────────────────────────────────────────────────────────┐
 * │ QueryControls (vehicle ID, from/to, asOf, includePoints, Run)        │
 * ├────────────────────────────────────────────────────────────────────────┤
 * │ TimelineStrip (horizontal bar of segments)                           │
 * ├──────────────────────────────────┬─────────────────────────────────────┤
 * │ EvidenceTable                   │ SegmentDetailsDrawer (when selected)│
 * │                                 │ SegmentMapPreview                   │
 * └──────────────────────────────────┴─────────────────────────────────────┘
 *
 * How to use:
 *   1. Enter a vehicle_entity_id (ULID). You can find IDs via the Truth
 *      Browser or GET /fleet-intelligence/vehicles/list
 *   2. Set a time window (default: last 24 h)
 *   3. Click "Run" to fetch the movement timeline
 *   4. Click a segment on the strip for full details
 *
 * Example vehicle IDs:
 *   - Check GET /fleet-intelligence/vehicles/list for real IDs
 *   - Or the vehicle_position_observed table in the Truth Browser
 */

import { useState, useCallback, useMemo } from 'react';
import { BarChart3, Filter } from 'lucide-react';
import QueryControls, { type QueryParams } from './QueryControls';
import TimelineStrip from './TimelineStrip';
import SegmentDetailsDrawer from './SegmentDetailsDrawer';
import EvidenceTable from './EvidenceTable';
import SegmentMapPreview from './SegmentMapPreview';
import { fetchVehicleMovementTimeline } from '../../../services/vehicleMovementTimelineApi';
import type { VehicleMovementTimelineResponse, MovementSegment } from '../../../types/vehicleMovementTimeline';

// ── State shapes ────────────────────────────────────────────────────────────

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: VehicleMovementTimelineResponse; queryParams: QueryParams };

// ── Component ───────────────────────────────────────────────────────────────

export default function VehicleMovementTimeline() {
  const [state, setState] = useState<FetchState>({ status: 'idle' });
  const [selectedSegment, setSelectedSegment] = useState<MovementSegment | null>(null);
  const [ordersOnly, setOrdersOnly] = useState(false);

  const handleRun = useCallback(async (params: QueryParams) => {
    setState({ status: 'loading' });
    setSelectedSegment(null);
    try {
      const data = await fetchVehicleMovementTimeline({
        vehicleId: params.vehicleId,
        from: params.from,
        to: params.to,
        asOf: params.asOf,
        includePoints: params.includePoints,
      });
      setState({ status: 'success', data, queryParams: params });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, []);

  const handleSelectSegment = useCallback((seg: MovementSegment | null) => {
    setSelectedSegment(seg);
  }, []);

  // Filtered segments for "orders only" toggle
  const filteredSegments = useMemo(() => {
    if (state.status !== 'success') return [];
    if (!ordersOnly) return state.data.segments;
    return state.data.segments.filter(
      (s) => (s.associated?.order_entity_ids?.length ?? 0) > 0,
    );
  }, [state, ordersOnly]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-success" />
        <h3 className="text-lg font-semibold text-white">Vehicle Movement Timeline</h3>
        <span className="text-xs text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">v0.2 Explorer</span>
      </div>

      {/* Query Controls */}
      <QueryControls
        onRun={handleRun}
        isLoading={state.status === 'loading'}
      />

      {/* Loading spinner */}
      {state.status === 'loading' && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-success border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-muted-foreground">Fetching timeline…</span>
        </div>
      )}

      {/* Error */}
      {state.status === 'error' && (
        <div className="bg-destructive/20 border border-destructive/30 rounded-lg p-4 text-sm text-destructive">
          <strong>Error:</strong> {state.message}
        </div>
      )}

      {/* Empty */}
      {state.status === 'success' && state.data.segments.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <p className="text-muted-foreground text-sm">No movement segments found for this vehicle in the given time window.</p>
          <p className="text-muted-foreground text-xs mt-1">Try widening the date range or check the vehicle entity ID.</p>
        </div>
      )}

      {/* Results */}
      {state.status === 'success' && state.data.segments.length > 0 && (
        <>
          {/* Meta summary bar */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span>
              Vehicle: <span className="text-foreground font-mono">{state.data.vehicle_entity_id}</span>
            </span>
            <span>
              Window: {new Date(state.data.window.from).toLocaleString('en-GB')} → {new Date(state.data.window.to).toLocaleString('en-GB')}
            </span>
            {state.data.window.asOf && (
              <span>
                asOf: <span className="text-warning">{new Date(state.data.window.asOf).toLocaleString('en-GB')}</span>
              </span>
            )}
            <span>
              Segments: <span className="text-foreground">{state.data.segments.length}</span>
            </span>
            {state.data.meta?.orders_summary && state.data.meta.orders_summary.unique_order_count > 0 && (
              <span>
                Orders: <span className="text-ring">{state.data.meta.orders_summary.unique_order_count}</span>
              </span>
            )}
            {state.data.meta?.projection_version && (
              <span>
                Projection: <span className="text-foreground">{state.data.meta.projection_version}</span>
              </span>
            )}
          </div>

          {/* Order filter toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOrdersOnly((prev) => !prev)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors ${
                ordersOnly
                  ? 'bg-ring text-white'
                  : 'bg-secondary text-muted-foreground hover:bg-accent'
              }`}
            >
              <Filter className="w-3 h-3" />
              {ordersOnly ? 'Showing segments with orders' : 'Show only segments with orders'}
            </button>
          </div>

          {/* Timeline Strip */}
          <TimelineStrip
            segments={filteredSegments}
            from={state.data.window.from}
            to={state.data.window.to}
            selectedSegmentId={selectedSegment?.segment_id ?? null}
            onSelectSegment={handleSelectSegment}
          />

          {/* Below: Evidence table + Details drawer side-by-side */}
          <div className="grid grid-cols-12 gap-4">
            {/* Left: Evidence table */}
            <div className={selectedSegment ? 'col-span-12 lg:col-span-7' : 'col-span-12'}>
              <EvidenceTable segments={filteredSegments} />
            </div>

            {/* Right: Segment details + map */}
            {selectedSegment && (
              <div className="col-span-12 lg:col-span-5 space-y-4">
                <SegmentDetailsDrawer
                  segment={selectedSegment}
                  meta={state.data.meta}
                  asOf={state.queryParams.asOf}
                  onClose={() => setSelectedSegment(null)}
                />
                <SegmentMapPreview segment={selectedSegment} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
